import { NgRedux } from '@ptl/angular-redux-store';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StateStructure } from '@ptl/state';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { CommonUtilityService } from '../../../../../shared/utility-services/common-utility.service';
import { setPreviousEncodedSessionMeds } from '../../../../medical-information/medication-information/reducers/drx-response.reducer';
import { MEDICATION_STATE_REDUX_ID, setMedicationState } from '../../../../medical-information/medication-information/reducers/medication.reducer';
import { DrxService } from '../../../../medical-information/medication-information/services/drx.service';
import { PHARMACY_REDUX_STATE_ID, setPharmacyState } from '../../../../medical-information/pharmacy-information/configs/reducers/pharmacy.reducer';
import { planDetailsByPlanId } from '../../../../plan-selection/configs/reducers/plan-details.reducer';
import { setActiveProductGroup, selectPlanForDRX, savePlanIdForEnrollment, savePlanNameForEnrollment } from '../../../../plan-selection/configs/reducers/plans.reducer';
import { PRODUCT_GROUPS } from '../../../../plan-selection/enums/product-groups.enum';
import { MedicareAdvantage_and_PDP_Product } from '../../../../plan-selection/interfaces/product.interface';
import { PlanUtilityService } from '../../../../plan-selection/plan-summary-list/plan-utility.service';
import { RetrievePlansService } from '../../../../plan-selection/plan-summary-list/retrieve-plans.service';
import { setEffectiveDate } from '../../configs/reducers/effective-date.reducer';
import { PtlSessionIndividualObject, PtlSessionIndividualResponse, PtlSessionObject, PtlSessionResponse } from '../interfaces/ptl-session.interface';
import { EffectiveDateStateStructure } from '../../configs/interfaces/effective-date.state-structure.interface';
import { DoctorVendorTypeState, DOCTOR_VENDOR_TYPE_STATE_REDUX_ID } from 'src/app/angular-elements/medical-information/doctor-information/configs/reducers/doctor-information.reducer';
import { DOCTOR_VENDOR_TYPE } from 'src/app/angular-elements/medical-information/doctor-information/configs/enums/doctor-vendor-type.enum';
import { DoctorStateSapphireDigital, DOCTOR_STATE_REDUX_ID_SAPPHIRE_DIGITAL, setDoctorState as setDoctorSapphireDigitalState} from 'src/app/angular-elements/medical-information/doctor-information-sapphire-digital/configs/reducers/doctors.reducer';
import { DoctorStateKyruus, DOCTOR_STATE_REDUX_ID_KYRUUS, setDoctorState as setDoctorKyruusState } from 'src/app/angular-elements/medical-information/doctor-information-kyruus/configs/reducers/doctors.reducer';
import { IndividualMarketSaveQuoteUtilityService } from 'src/app/angular-elements/producer/saved-quotes/services/individual-market-save-quote-utility.service';
@Injectable({
  providedIn: 'root'
})
export class PtlSessionService {

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private commonUtilityService: CommonUtilityService,
    private planUtilityService: PlanUtilityService,
    private drxService: DrxService,
    private httpClient: HTTPWrapperContext,
    private planService: RetrievePlansService,
    private individualMarketSaveQuoteUtilityService: IndividualMarketSaveQuoteUtilityService
  ) { }

  loadSeniorSessionByReferralId(referralId: string): Promise<PtlSessionResponse> {
    const requestConfigImplementation = new RequestConfigImplementation({
      url: '/sxesvc/api/v1/referralUpdate/retrievePlanSession',
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: {
        referralId
      }
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest(requestConfigImplementation);
  }

  loadIndividualSessionByReferralId(referralId: string): Promise<PtlSessionIndividualResponse> {
    const requestConfigImplementation = new RequestConfigImplementation({
      url: '/sxesvc/api/v1/referralUpdate/retrievePlanSession',
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: {
        referralId
      }
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest(requestConfigImplementation);
  }

  async applySessionFromState(stateObj: PtlSessionObject): Promise<boolean> {
    if (!stateObj?.productIds?.length) {
      console.error('No plan selected in session');
      return false;
    }
    if (!stateObj.productGroup) {
      console.error('No product group selected in session');
      return false;
    }

    const doctorVendorState: DoctorVendorTypeState = this.reducerUtilityService.getState(DOCTOR_VENDOR_TYPE_STATE_REDUX_ID);
    if (doctorVendorState.vendorType === DOCTOR_VENDOR_TYPE.KYRUUS) {
      this.ngRedux.dispatch(setDoctorKyruusState(stateObj.doctorState as DoctorStateKyruus));
    } else if (doctorVendorState.vendorType === DOCTOR_VENDOR_TYPE.SAPPHIRE_DIGITAL) {
      this.ngRedux.dispatch(setDoctorSapphireDigitalState(stateObj.doctorState as DoctorStateSapphireDigital));
    }
    
    this.ngRedux.dispatch(setMedicationState(stateObj.medicationInformation));
    this.ngRedux.dispatch(setPharmacyState(stateObj.pharmacyState.pharmacys, stateObj.pharmacyState.selectedPharmacy));
    this.ngRedux.dispatch(setPreviousEncodedSessionMeds('', 0));
    
    this.commonUtilityService.saveCountyZipData(stateObj.zipCountyState.county, stateObj.zipCountyState.zipCode);

    return await this.applyPlanFromState(stateObj);
  }

  async applyPlanFromState(stateObj: PtlSessionObject): Promise<boolean> {
    this.ngRedux.dispatch(setEffectiveDate(stateObj.effectiveDateState.effectiveDate));
    this.ngRedux.dispatch(setActiveProductGroup(stateObj.productGroup));

    await this.drxService.updateSession();
    let plan = await this.getSelectedPlan(stateObj);

    if (!plan) {
      console.error("no plan found for id ", stateObj.productIds[0]);
      return false;
    }

    this.ngRedux.dispatch(selectPlanForDRX(plan));
    this.ngRedux.dispatch(planDetailsByPlanId(plan.attributes.PLAN_ID));
    this.ngRedux.dispatch(savePlanIdForEnrollment(plan.attributes.PLAN_ID));
    this.ngRedux.dispatch(savePlanNameForEnrollment(plan.productName));

    return true;
  }

  async applyIndividualSessionFromState(stateObj: PtlSessionIndividualObject, referralId: string): Promise<void> {
    return await this.individualMarketSaveQuoteUtilityService.loadSaveQuoteDataToState({consumerInfo:{savedData: stateObj, referralId }});
  }

  captureIndividualSessionFromState(data: PtlSessionIndividualObject
  ): PtlSessionIndividualObject {
    let obj: PtlSessionIndividualObject = {
      agentName: data.agentName,
      planState: data.planState,
      planDetailsState: data.planDetailsState,
      individualMarketState: data.individualMarketState,
      enrollmentState: data.enrollmentState,
      zipCountyState: data.zipCountyState,
      lifeEventDocumentUploadState: data.lifeEventDocumentUploadState,
      effectiveDateState: data.effectiveDateState,
      saveQuoteName: data.saveQuoteName,
      redirectKey: data.location,
      agentId: data.agentId,
      agencyId: data.agencyId,
      saveQuoteData: data.saveQuoteData
    };

    if (data.specialEnrollmentCircumstanceState) {
      obj["special-enrollment-circumstance"] = data.specialEnrollmentCircumstanceState;
    }

    return obj;
  }

  captureSessionFromState(productIds: string[], productGroup: PRODUCT_GROUPS, year: string): PtlSessionObject {
    let effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    const doctorVendorState: DoctorVendorTypeState = this.reducerUtilityService.getState(DOCTOR_VENDOR_TYPE_STATE_REDUX_ID);
    let doctorState: DoctorStateKyruus | DoctorStateSapphireDigital = (() => {
      if (doctorVendorState.vendorType === DOCTOR_VENDOR_TYPE.KYRUUS) {
        return this.reducerUtilityService.getState(DOCTOR_STATE_REDUX_ID_KYRUUS);
      } else if (doctorVendorState.vendorType == DOCTOR_VENDOR_TYPE.SAPPHIRE_DIGITAL) {
        return this.reducerUtilityService.getState(DOCTOR_STATE_REDUX_ID_SAPPHIRE_DIGITAL);
      }
    })();

    return {
      productIds: productIds,
      productGroup: productGroup,
      doctorState,
      pharmacyState: this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID),
      medicationInformation: this.reducerUtilityService.getState(MEDICATION_STATE_REDUX_ID),
      effectiveDateState: { effectiveDate: year + '-01-01', effectiveDateYear: year, dirtyEffectiveDate: false },
      zipCountyState: this.reducerUtilityService.getState('ZipCounty')
    };
  }

  private async getSelectedPlan(payload: PtlSessionObject): Promise<MedicareAdvantage_and_PDP_Product> {
    await this.planService.getPlans({ [payload.productGroup]: [] }, +payload.effectiveDateState.effectiveDateYear);
    let allPlans = this.planUtilityService.getPlansByProductGroupAsArray(payload.productGroup);

    if (payload.productIds?.length) {
      return allPlans.filter((plan) => plan.productId === payload.productIds[0])[0] as MedicareAdvantage_and_PDP_Product;
    }
    else if (payload.planIds?.length){
      return allPlans.filter((plan) => plan.attributes.PLAN_ID === payload.planIds[0])[0] as MedicareAdvantage_and_PDP_Product;
    }

    return null;
  }
}
